import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppState } from 'AppProvider';
import { getExperiencePrice, getSessionPriceView } from 'components/helpers';
import { SkeletonLine } from 'components/theme/Skeleton/SkeletonLine';
import { STATUS_FAILURE, STATUS_IDLE, STATUS_PENDING } from 'types/app';
import { AVAILABLE_BRAND_FEATURES } from '@kouto/types';
import { currencyFormat, getCurrencyFromCode } from 'utils';
import getTotalPriceExcludingLast from 'utils/data/sessions/getTotalPriceExcludingLast';
import getUniqueDaySessions from 'utils/data/sessions/getUniqueDaySessions';
import { isFeatureActive } from 'utils/featureFlag';
import { calculateAdvertisedTierPrice } from 'utils/calculateAdvertisedTierPrice';
import { ITax } from 'types/experience.types';
import moment from 'moment';
import * as SC from '../styles';
import { SidebarPriceDisclaimer } from '../styles';

interface ISessionPriceProps {
  selectedSession: Record<string, unknown>;
  experience?: {
    loading: boolean;
    currency?: string;
    taxes?: ITax[];
    [key: string]: unknown;
  };
  currency?: string;
  hidePrice?: boolean;
  isPrivateBooking: boolean;
  hasPriceDisclaimer?: boolean;
  taxes?: ITax[];
}

const SessionPrice = ({
  selectedSession,
  experience,
  isPrivateBooking,
  hidePrice,
  currency,
  hasPriceDisclaimer,
  taxes,
}: ISessionPriceProps) => {
  const isRequestToBookEnabled = isFeatureActive(
    AVAILABLE_BRAND_FEATURES.ENABLE_REQUEST_BOOKINGS,
  );
  const showAdvertisedPrice = isFeatureActive(
    AVAILABLE_BRAND_FEATURES.ENABLE_SHOW_ADVERTISED_AMOUNT,
  );
  const pricedExperience = {
    ...experience,
    currency: currency ?? experience?.currency,
    taxes: taxes ?? experience?.taxes,
  };
  const { t: translate } = useTranslation();

  const {
    fetchSessionStatus,
    sessions,
    fetchFirstAvailabilityStatus,
    startDate,
    endDate,
  } = useAppState((state: Record<string, unknown>) => state.experience);

  const isFetchingSessionTiers =
    fetchSessionStatus === STATUS_IDLE || fetchSessionStatus === STATUS_PENDING;

  if (fetchFirstAvailabilityStatus === STATUS_FAILURE || hidePrice) {
    return null;
  }

  if (
    isFetchingSessionTiers ||
    selectedSession === null ||
    experience?.loading
  ) {
    return <SkeletonLine translucent style={{ height: '23px' }} />;
  }

  if (isRequestToBookEnabled) {
    const totalNights =
      startDate && endDate
        ? moment(endDate).diff(moment(startDate), 'days')
        : 0;

    const uniqueSessions = getUniqueDaySessions(sessions);
    const totalPrice = getTotalPriceExcludingLast(uniqueSessions);
    const displayPrice = calculateAdvertisedTierPrice(
      totalPrice,
      taxes ?? [],
      showAdvertisedPrice,
    );

    return (
      <>
        <span>
          <div>
            {totalNights === 0 ? translate('from ') : ''}
            {currencyFormat(getCurrencyFromCode(currency))(displayPrice)}
            &nbsp;/&nbsp;
            {totalNights === 0
              ? sessions[0]?.priceTiers[0]?.name
              : `${totalNights} ${translate(
                  totalNights === 1 ? 'night' : 'nights',
                )}`}
          </div>
        </span>
        {hasPriceDisclaimer && (
          <SidebarPriceDisclaimer>
            *{translate('priceDisclaimer')}
          </SidebarPriceDisclaimer>
        )}
      </>
    );
  }

  if (!isFetchingSessionTiers && sessions.length === 0) {
    return (
      <>
        <span>
          {getExperiencePrice(
            experience,
            translate,
            isPrivateBooking,
            showAdvertisedPrice,
          )}
        </span>
        {hasPriceDisclaimer && (
          <SidebarPriceDisclaimer>
            *{translate('priceDisclaimer')}
          </SidebarPriceDisclaimer>
        )}
      </>
    );
  }

  return (
    <SC.SidebarPrice>
      <span>
        {getSessionPriceView(
          selectedSession ?? sessions[0],
          pricedExperience,
          translate,
          isPrivateBooking,
          showAdvertisedPrice,
        )}
      </span>
      {}
      {hasPriceDisclaimer && (
        <SidebarPriceDisclaimer>
          *{translate('priceDisclaimer')}
        </SidebarPriceDisclaimer>
      )}
    </SC.SidebarPrice>
  );
};

export default SessionPrice;
